import { watch } from 'vue'

import { PROCESSING_STATUSES, TaskStatus } from './enums'
import { TASK_OBSERVATION_MAX_POLLS, TASK_TIMEOUT } from './const'
import type { TaskEntry } from './types'

export class TaskFailedError extends Error {
  readonly result: unknown

  constructor(task: TaskEntry<unknown>) {
    super(task.error || `Task ${task.status}`)
    this.result = task.result
  }
}

const SUCCEEDED_STATUSES = [TaskStatus.DONE, TaskStatus.SUCCEEDED]

export const isFinished = <T>(task?: TaskEntry<T>): task is TaskEntry<T> =>
  !!task && !PROCESSING_STATUSES.includes(task.status)

// Returns a task that succeeded and throws for one that failed. A task that is
// still processing, or not loaded yet, gives undefined.
const settle = <T>(task?: TaskEntry<T>) => {
  if (!isFinished(task)) return undefined
  if (!SUCCEEDED_STATUSES.includes(task.status)) throw new TaskFailedError(task)
  return task
}

// Resolves after `ms`, or earlier with the stored task once it has finished.
const waitForStoredTask = <T>(
  storedTask: () => TaskEntry<T> | undefined,
  ms: number,
  signal?: AbortSignal,
) => {
  let timer: ReturnType<typeof setTimeout> | undefined
  let stopWatch = () => {}
  let onAbort = () => {}
  return new Promise<TaskEntry<T> | undefined>((resolve, reject) => {
    onAbort = () => reject(signal?.reason)
    signal?.addEventListener('abort', onAbort, { once: true })
    timer = setTimeout(() => resolve(undefined), ms)
    stopWatch = watch(
      () => storedTask()?.status,
      () => {
        const task = storedTask()
        if (isFinished(task)) resolve(task)
      },
      { immediate: true },
    )
  }).finally(() => {
    clearTimeout(timer)
    stopWatch()
    signal?.removeEventListener('abort', onAbort)
  })
}

// The task list skips succeeded tasks and is paginated, so a submitted task is
// observed by its ID. Between requests, `storedTask` (the task store's copy,
// which a websocket push or a list poll can update) ends the wait as soon as
// the task finishes.
export const pollTask = async <T>(
  fetchTask: () => Promise<TaskEntry<T>>,
  {
    interval = TASK_TIMEOUT,
    signal,
    storedTask = () => undefined,
  }: {
    interval?: number
    signal?: AbortSignal
    storedTask?: () => TaskEntry<T> | undefined
  } = {},
): Promise<TaskEntry<T>> => {
  const known = settle(storedTask())
  if (known) return known
  for (let poll = 0; poll < TASK_OBSERVATION_MAX_POLLS; poll++) {
    const fetched = await fetchTask()
    signal?.throwIfAborted()
    const task = settle(fetched) ?? settle(await waitForStoredTask(storedTask, interval, signal))
    if (task) return task
  }
  throw new Error('Task completion confirmation timed out. Check the task before submitting again.')
}
